import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { STEPS } from "./FormKit";
import { CheckCircle2, AlertTriangle, GitBranch, ArrowRight } from "lucide-react";
import AvidaLogo from "@/components/brand/AvidaLogo";
import { useWizard } from "@/context/WizardContext";
import { allDhsIssues } from "@/lib/dhsRules";
import { fieldToStep } from "@/lib/fieldToStep";
import { recertChangesByStep } from "@/lib/recertChanges";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

export default function Sidebar({ currentStep, maxReached = 10 }) {
  const { data } = useWizard();

  const issuesByStep = useMemo(() => {
    const out = {};
    for (const issue of allDhsIssues(data)) {
      const t = fieldToStep(issue.field);
      if (!t) continue;
      const bucket = out[t.step] || (out[t.step] = { error: 0, warning: 0 });
      if (issue.severity === "error") bucket.error += 1;
      else if (issue.severity === "warning") bucket.warning += 1;
    }
    return out;
  }, [data]);

  // Per-step "changed since last visit" — full {count, items[]} per step
  // so the Sidebar can render counts AS BADGES and details AS POPOVERS.
  // Stays at zero for first-time visits (no recert baseline).
  const recertChanges = useMemo(() => recertChangesByStep(data), [data]);
  const isRecert = recertChanges.total > 0 || !!(data?._visit?.is_recert);

  return (
    <aside
      className="hidden lg:flex flex-col w-[320px] min-h-screen sticky top-0 bg-white border-r border-[#D6E3EF]"
      data-testid="wizard-sidebar"
    >
      {/* Brand lockup — thin blue-to-blue gradient strip at the very top */}
      <div className="brand-strip-thin px-6 py-4">
        <Link to="/" className="flex items-center gap-3" data-testid="sidebar-home-link">
          <div className="rounded-lg bg-white p-1 border border-white/20">
            <AvidaLogo size="xs" iconOnly />
          </div>
          <div className="pl-3 border-l border-white/20">
            <div className="font-display text-[14px] font-semibold leading-tight text-white tracking-tight">
              Avida Health
            </div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-[#FFDA0B] mt-0.5 font-bold">
              NSA Workflow
            </div>
          </div>
        </Link>
      </div>

      {/* Progress header */}
      <div className="px-6 pt-7 pb-4">
        <div className="eyebrow mb-2">Progress</div>
        <div className="font-display text-[22px] font-semibold text-[#0B1E3A] leading-tight tracking-tight">
          Step {currentStep} <span className="text-[#B7CADA]">of 10</span>
        </div>
        {/* Flat yellow progress rail — NO glow */}
        <div className="mt-3 h-1.5 w-full rounded-full bg-[#E7EEF3] overflow-hidden">
          <div
            className="h-full rounded-full"
            style={{
              width: `${(currentStep / 10) * 100}%`,
              backgroundColor: "#FFDA0B",
            }}
          />
        </div>
        {isRecert && (
          <div
            className="mt-4 px-3 py-2.5 rounded-lg bg-[#E7F1FB] border border-[#C1D7EF] flex items-center gap-2"
            data-testid="sidebar-recert-summary"
          >
            <GitBranch className="w-3.5 h-3.5 text-[#0A3D79] flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-[10.5px] uppercase tracking-[0.14em] font-bold text-[#0A3D79]">Re-cert visit</div>
              <div className="text-[12px] text-[#0B1E3A] mt-0.5 leading-tight" data-testid="sidebar-recert-total">
                {recertChanges.total === 0
                  ? "No changes vs last visit yet"
                  : `${recertChanges.total} change${recertChanges.total === 1 ? "" : "s"} vs last visit`}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Steps list */}
      <nav className="flex flex-col relative px-4 pt-3 pb-6 stagger-in" aria-label="Wizard steps">
        <div className="absolute left-[27px] top-5 bottom-5 w-px bg-[#E7EEF3]" />
        {STEPS.map((s) => {
          const isActive = s.id === currentStep;
          const isDone = s.id < currentStep;
          const clickable = s.id <= maxReached;
          const bucket = issuesByStep[s.id];
          const hasError = bucket && bucket.error > 0;
          const hasWarn = bucket && !hasError && bucket.warning > 0;

          const content = (
            <div
              className={`flex items-start gap-3 py-3 group relative rounded-lg px-2 transition ${
                isActive ? "bg-[#FFFDEC]" : "hover:bg-[#F0F6FC]"
              }`}
              data-testid={`sidebar-step-${s.id}`}
            >
              <div className="relative z-10 flex-shrink-0 mt-0.5">
                {isDone ? (
                  <div className="w-7 h-7 rounded-full bg-[#0A3D79] flex items-center justify-center">
                    <CheckCircle2 className="w-[15px] h-[15px] text-white" strokeWidth={2.5} />
                  </div>
                ) : isActive ? (
                  <div className="w-7 h-7 rounded-full flex items-center justify-center bg-[#FFDA0B] border border-[#DBB90A]">
                    <span className="text-[11px] font-bold text-[#072A54]">{s.id}</span>
                  </div>
                ) : (
                  <div className="w-7 h-7 rounded-full border border-[#D6E3EF] bg-white flex items-center justify-center">
                    <span className="text-[11px] font-semibold text-[#8AA0BD]">{s.id}</span>
                  </div>
                )}
                {(hasError || hasWarn) && (
                  <span
                    className={`absolute -top-0.5 -right-0.5 w-3 h-3 rounded-full ring-2 ring-white ${
                      hasError ? "bg-[#c05746]" : "bg-[#DBB90A]"
                    }`}
                    aria-hidden="true"
                  />
                )}
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <div
                    className={`text-[14px] font-semibold leading-tight ${
                      isActive ? "text-[#0B1E3A]" : isDone ? "text-[#1E2E4A]" : "text-[#3A5275]"
                    }`}
                  >
                    {s.title}
                  </div>
                  {(hasError || hasWarn) && (
                    <span
                      className={`inline-flex items-center gap-0.5 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full ${
                        hasError
                          ? "bg-[#fbeae5] text-[#c05746]"
                          : "bg-[#FFFDEC] text-[#8A6B00] border border-[#DBB90A]/40"
                      }`}
                      data-testid={`sidebar-step-${s.id}-badge`}
                      title={
                        hasError
                          ? `${bucket.error} DHS error${bucket.error > 1 ? "s" : ""} to resolve`
                          : `${bucket.warning} DHS warning${bucket.warning > 1 ? "s" : ""}`
                      }
                    >
                      <AlertTriangle className="w-2.5 h-2.5" />
                      {hasError ? bucket.error : bucket.warning}
                    </span>
                  )}
                  {isRecert && (recertChanges[s.id]?.count || 0) > 0 && (
                    <span
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
                      onMouseDown={(e) => e.stopPropagation()}
                    >
                      <Popover>
                        <PopoverTrigger asChild>
                          <button
                            type="button"
                            className="inline-flex items-center gap-0.5 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-[#E7F1FB] text-[#0A3D79] border border-[#C1D7EF] hover:bg-[#d6e6f3] transition-colors"
                            data-testid={`sidebar-step-${s.id}-recert-changed`}
                            title={`${recertChanges[s.id].count} field${recertChanges[s.id].count > 1 ? "s" : ""} changed since last visit — click to see details`}
                          >
                            <GitBranch className="w-2.5 h-2.5" />
                            {recertChanges[s.id].count}
                          </button>
                        </PopoverTrigger>
                        <PopoverContent
                          side="right" align="start"
                          className="w-[320px] p-0 border-[#D6E3EF] shadow-xl"
                          data-testid={`sidebar-step-${s.id}-recert-popover`}
                        >
                          <div className="px-4 pt-3 pb-2 border-b border-[#E7EEF5] bg-[#F7FAFC]">
                            <div className="text-[10px] uppercase tracking-[0.14em] font-bold text-[#0A3D79]">
                              Step {s.id} · {s.label}
                            </div>
                            <div className="text-[12.5px] text-[#0B1E3A] mt-0.5 font-semibold">
                              {recertChanges[s.id].count} change{recertChanges[s.id].count > 1 ? "s" : ""} since last visit
                            </div>
                          </div>
                          <ul className="max-h-[280px] overflow-y-auto py-2">
                            {recertChanges[s.id].items.map((item, idx) => (
                              <li
                                key={`${item.label || "row"}::${idx}`}
                                className="px-4 py-1.5 text-[12px] text-[#0B1E3A] border-b border-[#F0F4F8] last:border-b-0"
                                data-testid={`sidebar-step-${s.id}-recert-item-${idx}`}
                              >
                                <div className="font-semibold text-[12px] truncate">{item.label}</div>
                                {(item.before !== undefined || item.after !== undefined) && (
                                  <div className="flex items-center gap-1 text-[11px] text-[#3A5275] mt-0.5 font-mono">
                                    <span className="line-through opacity-70 truncate max-w-[120px]">{item.before || "—"}</span>
                                    <ArrowRight className="w-3 h-3 text-[#0A3D79] flex-shrink-0" />
                                    <span className="text-[#0A3D79] font-semibold truncate max-w-[120px]">{item.after || "—"}</span>
                                  </div>
                                )}
                              </li>
                            ))}
                          </ul>
                        </PopoverContent>
                      </Popover>
                    </span>
                  )}
                </div>
                <div
                  className={`text-[12px] mt-0.5 leading-snug ${
                    isActive ? "text-[#3A5275]" : "text-[#8AA0BD]"
                  }`}
                >
                  {s.blurb}
                </div>
              </div>
            </div>
          );

          return clickable ? (
            <Link key={s.id} to={`/wizard/${s.id}`}>
              {content}
            </Link>
          ) : (
            <div key={s.id} className="opacity-50 pointer-events-none">{content}</div>
          );
        })}
      </nav>

      {/* Footer */}
      <div className="mt-auto px-6 py-5 border-t border-[#E7EEF3] bg-[#F7FAFC]">
        <div className="flex items-center justify-between">
          <span className="avida-accent-chip">Wisconsin · DHS 107.112</span>
          <span className="text-[10px] uppercase tracking-[0.16em] text-[#8AA0BD] font-semibold">v2.0</span>
        </div>
      </div>
    </aside>
  );
}
